// apps/client/src/pages/OnboardingScreen.tsx
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../store/userStore';
import { Toggle } from '../ui-kit/Toggle';
import { HookAnimationSVG, CoinStackSVG, TreasureChestSVG } from '../ui-kit/OnboardingSVGs';

export function OnboardingScreen() {
    const { t } = useTranslation();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [showError, setShowError] = useState(false);

    const completeOnboarding = useUserStore((s) => s.completeOnboarding);
    const acceptLegal = useUserStore((s) => s.acceptLegal);

    const canContinue = currentSlide < 2 ? true : (acceptTerms && acceptPrivacy);

    const nextSlide = () => {
        if (currentSlide < 2) {
            setCurrentSlide(prev => prev + 1);
        } else {
            if (!canContinue) {
                setShowError(true);
                return;
            }
            setShowError(false);
            acceptLegal();
            completeOnboarding();
        }
    };

    const handleSkip = () => {
        // Jump straight to the legal slide
        setCurrentSlide(2);
    };

    const slides = [
        {
            id: 'hook',
            svg: <HookAnimationSVG />,
            title: t('onboarding.hookTitle'),
            description: t('onboarding.hookDesc')
        },
        {
            id: 'loop',
            svg: <CoinStackSVG />,
            title: t('onboarding.loopTitle'),
            description: t('onboarding.loopDesc')
        },
        {
            id: 'reward',
            svg: <TreasureChestSVG />,
            title: t('onboarding.rewardTitle'),
            description: t('onboarding.rewardDesc')
        }
    ];

    return (
        <div className="min-h-[100dvh] bg-[#050210] flex flex-col items-center justify-between p-6 pb-10 overflow-hidden relative">
            {/* Background Glows */}
            <div className="absolute top-0 left-0 w-full h-[50vh] bg-gradient-to-b from-[#3D2B79]/30 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-full h-[50vh] bg-gradient-to-t from-[#2563EB]/10 to-transparent pointer-events-none" />

            {/* Skip button — top right */}
            <div className="w-full flex justify-end z-20">
                {currentSlide < 2 && (
                    <button
                        onClick={handleSkip}
                        className="text-white/40 text-sm font-bold uppercase tracking-wider hover:text-white/70 transition-colors"
                    >
                        {t('onboarding.skip')}
                    </button>
                )}
            </div>

            {/* Carousel Content */}
            <div className="flex-1 w-full max-w-sm flex flex-col justify-center relative z-10">
                <AnimatePresence mode="wait">
                    {(() => {
                        const slide = slides[currentSlide] || slides[0]!;
                        return (
                            <motion.div
                                key={currentSlide}
                                initial={{ opacity: 0, x: 50, scale: 0.95 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: -50, scale: 0.95 }}
                                transition={{ duration: 0.4, type: "spring", bounce: 0.3 }}
                                className="flex flex-col items-center text-center w-full"
                            >
                                {/* 3D SVG Hero */}
                                <div className="mb-10 w-full flex justify-center">
                                    {slide.svg}
                                </div>

                                {/* Title */}
                                <h1 className="text-3xl font-black text-white mb-4 font-display leading-tight drop-shadow-md">
                                    {slide.title}
                                </h1>

                                {/* Subtitle */}
                                <p className="text-white/70 text-base leading-relaxed mb-6 font-medium">
                                    {slide.description}
                                </p>

                                {/* Legal terms only on last slide */}
                                {currentSlide === 2 && (
                                    <div className={`w-full space-y-3 mt-4 text-left bg-white/5 border border-white/10 p-4 rounded-2xl backdrop-blur-md transition-all ${showError ? 'ring-2 ring-red-500/50 animate-shake' : ''}`}>
                                        <Toggle
                                            checked={acceptTerms}
                                            onChange={(v) => { setAcceptTerms(v); setShowError(false); }}
                                            label={<span className="text-xs">{t('onboarding.legalAge')}<a href="https://bingoton.app/privacy" target="_blank" className="underline font-black text-[#FFC107]">{t('onboarding.legalRules')}</a>.</span>}
                                            error={showError && !acceptTerms}
                                        />
                                        <Toggle
                                            checked={acceptPrivacy}
                                            onChange={(v) => { setAcceptPrivacy(v); setShowError(false); }}
                                            label={<span className="text-xs">{t('onboarding.privacyAccept')}<a href="https://bingoton.app/privacy" target="_blank" className="underline font-black text-[#FFC107]">{t('onboarding.privacyPolicy')}</a>.</span>}
                                            error={showError && !acceptPrivacy}
                                        />
                                    </div>
                                )}
                            </motion.div>
                        );
                    })()}
                </AnimatePresence>
            </div>

            {/* CTA & Pagination — bottom */}
            <div className="w-full max-w-sm space-y-6 z-20 mt-4">
                {/* Dots for perceived progress */}
                <div className="flex justify-center gap-2">
                    {[0, 1, 2].map(dotIndex => (
                        <div
                            key={dotIndex}
                            className={`h-2 rounded-full transition-all duration-300 ${currentSlide === dotIndex ? 'w-8 bg-[#FFC107] shadow-[0_0_10px_rgba(255,193,7,0.5)]' : 'w-2 bg-white/20'}`}
                        />
                    ))}
                </div>

                {/* Massive CTA Button */}
                <motion.button
                    whileTap={canContinue ? { scale: 0.96 } : {}}
                    onClick={nextSlide}
                    className={`
                        w-full py-5 rounded-[20px] font-black text-lg uppercase tracking-wider transition-all
                        ${canContinue && currentSlide === 2
                            ? 'bg-gradient-to-r from-[#FFC107] to-[#F59E0B] text-[#1a0b2e] shadow-[0_10px_30px_rgba(255,193,7,0.4)] hover:shadow-[0_10px_40px_rgba(255,193,7,0.6)]'
                            : currentSlide < 2
                                ? 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-lg border border-white/20'
                                : 'bg-white/5 text-white/30 cursor-not-allowed border border-white/10 border-dashed'
                        }
                    `}
                >
                    {currentSlide < 2 ? t('onboarding.next') : t('onboarding.start')}
                </motion.button>
            </div>
        </div>
    );
}
